import React, { useState, useEffect } from 'react'

export interface ScheduleFormData {
  sessionNumber: number
  title: string
  description: string
  dateTime: string
  durationMinutes: number
  location: string
  meetingLink: string
  type: 'theory' | 'practical'
}

interface ScheduleFormProps {
  initialData?: ScheduleFormData
  onSubmit: (data: ScheduleFormData) => void
  onCancel: () => void
  isLoading?: boolean
}

export const ScheduleForm: React.FC<ScheduleFormProps> = ({ initialData, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState<ScheduleFormData>(initialData || {
    sessionNumber: 1,
    title: '',
    description: '',
    dateTime: '',
    durationMinutes: 120,
    location: '',
    meetingLink: '',
    type: 'practical',
  })

  // Update when initialData changes (useful when the same modal is kept open for different edits)
  useEffect(() => {
    if (initialData) {
      setFormData(initialData)
    }
  }, [initialData])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  const inputNoIconClass = "w-full px-4 py-3 bg-slate-50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/10 focus:border-primary/50 focus:ring-4 focus:ring-primary/5 rounded-xl text-sm font-bold text-slate-900 dark:text-white transition-all outline-none placeholder:text-slate-400 placeholder:font-medium shadow-sm"
  const labelClass = "block text-sm font-black text-slate-500 dark:text-slate-400 mb-1.5 ml-1"

  return (
    <form onSubmit={handleSubmit} className="space-y-4 animate-in fade-in zoom-in-95 duration-200">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-1">
          <label className={labelClass}>Sesi Ke <span className="text-primary">*</span></label>
          <input type="number" min={1} value={formData.sessionNumber}
            onChange={(e) => setFormData(prev => ({ ...prev, sessionNumber: parseInt(e.target.value) || 1 }))}
            className={inputNoIconClass} required />
        </div>
        <div className="md:col-span-3">
          <label className={labelClass}>Judul Sesi <span className="text-primary">*</span></label>
          <input type="text" value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Contoh: Orientasi & Pengenalan"
            className={inputNoIconClass} required />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className={labelClass}>Tipe</label>
          <select value={formData.type}
            onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'theory' | 'practical' }))}
            className={`${inputNoIconClass} appearance-none cursor-pointer`}>
            <option value="theory">Teori</option>
            <option value="practical">Praktik</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Tanggal & Jam <span className="text-primary">*</span></label>
          <input type="datetime-local" value={formData.dateTime}
            onChange={(e) => setFormData(prev => ({ ...prev, dateTime: e.target.value }))}
            className={inputNoIconClass + " [color-scheme:light] dark:[color-scheme:dark]"} required />
        </div>
        <div>
          <label className={labelClass}>Durasi (menit)</label>
          <input type="number" min={30} step={30} value={formData.durationMinutes}
            onChange={(e) => setFormData(prev => ({ ...prev, durationMinutes: parseInt(e.target.value) || 120 }))}
            className={inputNoIconClass} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Lokasi Sesi <span className="text-slate-400 font-medium normal-case">(opsional)</span></label>
          <input type="text" value={formData.location}
            onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
            placeholder="Kosongkan jika sama dengan lokasi batch"
            className={inputNoIconClass} />
        </div>
        <div>
          <label className={labelClass}>Link Meeting <span className="text-slate-400 font-medium normal-case">(opsional)</span></label>
          <input type="url" value={formData.meetingLink}
            onChange={(e) => setFormData(prev => ({ ...prev, meetingLink: e.target.value }))}
            placeholder="https://meet.google.com/..."
            className={inputNoIconClass} />
        </div>
      </div>

      <div>
        <label className={labelClass}>Deskripsi Sesi</label>
        <textarea value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={3} placeholder="Apa yang akan dipelajari di sesi ini..."
          className={`${inputNoIconClass} resize-none`} />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-white/5 mt-6">
        <button type="button" onClick={onCancel}
          className="px-6 py-3 text-slate-500 dark:text-slate-400 text-sm font-bold hover:text-slate-900 dark:hover:text-white transition-all">
          Batal
        </button>
        <button type="submit" disabled={isLoading}
          className="px-8 py-3.5 bg-primary text-white rounded-xl text-sm font-bold hover:opacity-90 transition-all shadow-xl shadow-primary/20 disabled:opacity-50">
          {isLoading ? 'Menyimpan...' : 'Simpan Sesi'}
        </button>
      </div>
    </form>
  )
}
